/**
 * ImportTab - CSV Import UI
 * Extracted from DevPage.tsx for modularity
 */

import { useState, useCallback, useRef } from 'react';
import { Card, CardHeader, Button } from '@/components/shared';
import { importOneMoneyCSV, ImportResult, ImportProgress } from '@services/import';
import { devlog } from '@services/devlog';

interface ImportOptionsState {
  useFooterBalances: boolean;
  skipErrorFix: boolean;
  detectRecurring: boolean;
}

interface ImportTabProps {
  onImportComplete: () => void;
}

export function ImportTab({ onImportComplete }: ImportTabProps) {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [options, setOptions] = useState<ImportOptionsState>({
    useFooterBalances: true,
    skipErrorFix: false,
    detectRecurring: true,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setImporting(true);
      setResult(null);
      setProgress({ current: 0, total: 0, status: 'Starting...' });

      devlog.info('import', 'User initiated CSV import', {
        filename: file.name,
        size: file.size,
        options,
      });

      try {
        const importResult = await importOneMoneyCSV(file, setProgress, options);
        setResult(importResult);

        if (importResult.success) {
          devlog.info('import', 'Import successful', {
            imported: importResult.imported,
            errorFixCount: importResult.errorFixCount,
            recurringDetected: importResult.recurringDetected,
            balancesApplied: importResult.balancesApplied,
            newAccounts: importResult.newAccounts,
            newCategories: importResult.newCategories,
          });
        } else {
          devlog.error('import', 'Import failed', { errors: importResult.errors });
        }

        onImportComplete();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        devlog.error('import', 'Import exception', { error: errorMsg });
        setResult({
          success: false,
          imported: 0,
          skipped: 0,
          errorFixCount: 0,
          errorFixTotal: 0,
          recurringDetected: 0,
          errors: [errorMsg],
          warnings: [],
          newAccounts: [],
          newCategories: [],
          footerBalances: new Map(),
          balancesApplied: false,
        });
      } finally {
        setImporting(false);
        setProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [onImportComplete, options]
  );

  const handleOptionChange = (key: keyof ImportOptionsState) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="tab-content">
      <Card>
        <CardHeader
          title="Import from 1Money CSV"
          subtitle="Upload your 1Money export file. Supports ~500KB+ files with thousands of transactions."
        />

        <div className="import-info">
          <h4>Expected CSV format:</h4>
          <code>DATE,TYPE,FROM ACCOUNT,TO ACCOUNT/TO CATEGORY,AMOUNT,CURRENCY,NOTES</code>
          <p>
            <strong>Note:</strong> Import ADDS to existing data. Use &quot;Clear All Data&quot; first if you
            want a fresh start.
          </p>
        </div>

        {/* Import Options */}
        <div className="import-options">
          <h4>Import Options:</h4>
          <label className="option-checkbox">
            <input
              type="checkbox"
              checked={options.useFooterBalances}
              onChange={() => handleOptionChange('useFooterBalances')}
              disabled={importing}
            />
            <span>Use footer balances (recommended)</span>
            <span className="option-hint">Use actual balances from CSV footer instead of calculating</span>
          </label>
          <label className="option-checkbox">
            <input
              type="checkbox"
              checked={options.skipErrorFix}
              onChange={() => handleOptionChange('skipErrorFix')}
              disabled={importing}
            />
            <span>Skip error fix transactions</span>
            <span className="option-hint">Skip transactions marked as balance corrections</span>
          </label>
          <label className="option-checkbox">
            <input
              type="checkbox"
              checked={options.detectRecurring}
              onChange={() => handleOptionChange('detectRecurring')}
              disabled={importing}
            />
            <span>Detect recurring transactions</span>
            <span className="option-hint">Tag bills like Xfinity, Spotify, car payments, etc.</span>
          </label>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        <div className="import-actions">
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Select CSV File'}
          </Button>
        </div>

        {/* Progress */}
        {progress && (
          <div className="import-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: progress.total > 0 ? `${(progress.current / progress.total) * 100}%` : '0%',
                }}
              />
            </div>
            <div className="progress-text">{progress.status}</div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className={`import-result ${result.success ? 'success' : 'error'}`}>
            <h4>{result.success ? '[OK] Import Successful' : '[FAILED] Import Failed'}</h4>

            {result.success && (
              <div className="result-details">
                <p>
                  <strong>{result.imported}</strong> transactions imported
                </p>
                {result.skipped > 0 && (
                  <p>
                    <strong>{result.skipped}</strong> rows skipped
                  </p>
                )}

                {/* Error Fix Detection */}
                {result.errorFixCount > 0 && (
                  <p className="result-warning">
                    <strong>{result.errorFixCount}</strong> error fix transactions detected (
                    ${result.errorFixTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })})
                    {options.skipErrorFix ? ' - skipped' : ' - imported'}
                  </p>
                )}

                {/* Recurring Detection */}
                {result.recurringDetected > 0 && (
                  <p>
                    <strong>{result.recurringDetected}</strong> recurring transactions tagged
                  </p>
                )}

                {/* Footer Balances */}
                {result.footerBalances.size > 0 && (
                  <p>
                    <strong>{result.footerBalances.size}</strong> account balances from CSV footer
                    {result.balancesApplied ? ' (applied)' : ' (not applied)'}
                  </p>
                )}

                {result.newAccounts.length > 0 && (
                  <p>
                    <strong>{result.newAccounts.length}</strong> new accounts created:{' '}
                    {result.newAccounts.join(', ')}
                  </p>
                )}
                {result.newCategories.length > 0 && (
                  <p>
                    <strong>{result.newCategories.length}</strong> new categories created:{' '}
                    {result.newCategories.slice(0, 10).join(', ')}
                    {result.newCategories.length > 10 && ` ...and ${result.newCategories.length - 10} more`}
                  </p>
                )}
              </div>
            )}

            {/* Warnings */}
            {result.warnings && result.warnings.length > 0 && (
              <div className="result-warnings">
                <h5>Warnings ({result.warnings.length}):</h5>
                <ul>
                  {result.warnings.slice(0, 10).map((warn, i) => (
                    <li key={i}>{warn}</li>
                  ))}
                  {result.warnings.length > 10 && (
                    <li>...and {result.warnings.length - 10} more warnings</li>
                  )}
                </ul>
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="result-errors">
                <h5>Errors ({result.errors.length}):</h5>
                <ul>
                  {result.errors.slice(0, 20).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {result.errors.length > 20 && (
                    <li>...and {result.errors.length - 20} more errors (see /log for full details)</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
